import { lazy, Suspense, useEffect, useState } from "react";
import { startFlowTelemetry, setFlowPoster } from "./services/flowTelemetry";
import { postFlowEvents } from "./services/flowApi";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AuthBridge } from "./context/AuthBridge";
import { ColorSchemeProvider } from "./context/ColorSchemeContext";
import { FeatureThemeProvider } from "./context/FeatureThemeContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { AccountProvider } from "./context/AccountContext";
import { ToastProvider } from "./context/ToastContext";
import { AppLayout } from "./components/layout/AppLayout";
import { Spinner } from "./components/common/Spinner";
import { LoginScreen } from "./pages/LoginScreen";
import { AuthBlockedScreen } from "./pages/AuthBlockedScreen";
import { OnboardingTour } from "./components/theme/OnboardingTour";
import { WhatsNewModal } from "./components/theme/WhatsNewModal";
import { ThemeNudgeModal } from "./components/theme/ThemeNudgeModal";

import { DashboardPage } from "./pages/DashboardPage";
/* ForecastPage is NOT imported -- the route redirects while the feature is
   hidden (#449), and importing a component nothing renders would leave an
   unused binding that lint is right to complain about.

   The FILE stays on purpose. It is the page #449 restores, and deleting it
   would mean rewriting it from the issue text later. It is therefore a
   DELIBERATE orphan: nothing imports it, which normally means invisible to
   the build and untested -- recorded here so the next orphan sweep finds
   this comment rather than a mystery. */
import { onboardingApi } from "./services/onboardingApi";
import { otherAppApi } from "./services/userApi";

/* PAGES LOAD WHEN YOU GO TO THEM (#556/#557).
   The whole app was one 1.7 MB script (477 KB gzipped) that every sign-in
   downloaded and parsed before the first screen, including 25 pages nobody
   had asked for yet. The dashboard stays in the main bundle because it is the
   first screen for almost everyone. */
const FirstRunPage = lazy(() => import("./pages/FirstRunPage").then((m) => ({ default: m.FirstRunPage })));
const HiveListPage = lazy(() => import("./pages/HiveListPage").then((m) => ({ default: m.HiveListPage })));
const AddHivePage = lazy(() => import("./pages/AddHivePage").then((m) => ({ default: m.AddHivePage })));
const HiveDetailPage = lazy(() => import("./pages/HiveDetailPage").then((m) => ({ default: m.HiveDetailPage })));
const HoneyPoolPage = lazy(() => import("./pages/business/HoneyPoolPage").then((m) => ({ default: m.HoneyPoolPage })));
const VendorsPage = lazy(() => import("./pages/business/VendorsPage").then((m) => ({ default: m.VendorsPage })));
const ClientDetailPage = lazy(() => import("./pages/business/ClientDetailPage").then((m) => ({ default: m.ClientDetailPage })));
const ExpendituresPage = lazy(() => import("./pages/business/ExpendituresPage").then((m) => ({ default: m.ExpendituresPage })));
const OtherProductsPage = lazy(() => import("./pages/business/OtherProductsPage").then((m) => ({ default: m.OtherProductsPage })));
const HarvestsPage = lazy(() => import("./pages/business/HarvestsPage").then((m) => ({ default: m.HarvestsPage })));
const EquipmentPage = lazy(() => import("./pages/business/EquipmentPage").then((m) => ({ default: m.EquipmentPage })));
const SuppliersPage = lazy(() => import("./pages/business/SuppliersPage").then((m) => ({ default: m.SuppliersPage })));
const PartnersPage = lazy(() => import("./pages/PartnersPage").then((m) => ({ default: m.PartnersPage })));
const RecurringOrdersPage = lazy(() => import("./pages/business/RecurringOrdersPage").then((m) => ({ default: m.RecurringOrdersPage })));
const PnLPage = lazy(() => import("./pages/business/PnLPage").then((m) => ({ default: m.PnLPage })));
const TeamDashboardPage = lazy(() => import("./pages/team/TeamDashboardPage").then((m) => ({ default: m.TeamDashboardPage })));
const TeamTasksPage = lazy(() => import("./pages/team/TeamTasksPage").then((m) => ({ default: m.TeamTasksPage })));
const JournalPage = lazy(() => import("./pages/JournalPage").then((m) => ({ default: m.JournalPage })));
const ImportPage = lazy(() => import("./pages/ImportPage").then((m) => ({ default: m.ImportPage })));
const BloomCalendarPage = lazy(() => import("./pages/BloomCalendarPage").then((m) => ({ default: m.BloomCalendarPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const SupportPage = lazy(() => import("./pages/SupportPage").then((m) => ({ default: m.SupportPage })));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const InspectionChecklistsPage = lazy(() => import("./pages/InspectionChecklistsPage").then((m) => ({ default: m.InspectionChecklistsPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));

function AuthedApp() {
  const { authBlock } = useAuth();

  /*
   * Has this account been through first-run setup? (#222)
   *
   * Asked of the SERVER, not of localStorage. The existing OnboardingTour
   * remembers itself per browser, which is fine for a tour nobody has to
   * finish -- but setup creates real rows, and "have I set up my apiary"
   * must not come out differently on a second device or after clearing
   * site data.
   *
   * null while unknown: rendering the dashboard first and then replacing
   * it with a setup screen is a flash of the wrong app, and rendering
   * setup first would show it to every established user for a moment.
   */
  const [firstRun, setFirstRun] = useState(null);

  /* #379. The flush timer and the page-away hook, started once for the
     whole app -- one buffer, because two would each flush half the
     events and neither would know about the other's open flows.

     Outside the auth check on purpose: the flows worth measuring most
     are onboarding and import, which happen to a brand new account, and
     gating the timer behind a settled session would miss them. Nothing
     is sent until there is something to send. */
  useEffect(() => {
    /* The transport is handed in rather than imported by the telemetry
       module -- see setFlowPoster for why a measurement must not decide
       what every instrumented page depends on. */
    setFlowPoster(postFlowEvents);
    return startFlowTelemetry();
  }, []);

  useEffect(() => {
    if (authBlock) return;
    let cancelled = false;
    /* Leave a breadcrumb that this app was opened (#302), so the OTHER
       one knows whether to mention itself. Once per session, best-effort,
       nothing waits for it: without this the reminder cannot tell a
       first-timer from somebody who works on both every day. */
    otherAppApi.appSeen("web").catch(() => {});
    onboardingApi
      .state()
      .then((r) => { if (!cancelled) setFirstRun(r); })
      // Best effort. If this call fails the app must still open -- being
      // unable to ask whether setup is needed is not a reason to withhold
      // an apiary somebody already has.
      .catch(() => { if (!cancelled) setFirstRun({ needed: false }); });
    return () => { cancelled = true; };
  }, [authBlock]);

  // Ahead of the providers deliberately. FeatureThemeProvider and
  // SubscriptionProvider both fetch on mount, so leaving them mounted
  // against a dead-end 401 means the same rejection over and over behind a
  // screen that is only there to explain it once (#166).
  if (authBlock) return <AuthBlockedScreen />;

  if (firstRun === null) return null;

  return (
    <AccountProvider>
    <FeatureThemeProvider>
      <SubscriptionProvider>
        {firstRun.needed ? (
          /* Instead of the shell, not inside it. A setup screen wrapped in
             navigation invites wandering off into pages that are empty
             precisely because setup has not happened yet. */
          <FirstRunPage
            hiveLimit={firstRun.hive_limit}
            onDone={(r) => {
              setFirstRun({ needed: false });
              // Land on the hive, not the dashboard: the dashboard of a
              // one-hive apiary is a summary of one thing.
              if (r?.land_on_hive_id) window.location.assign(`/hives/${r.land_on_hive_id}`);
            }}
          />
        ) : (
        <AppLayout>
          <OnboardingTour />
          <WhatsNewModal />
          <ThemeNudgeModal />
          <Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/hives" element={<HiveListPage />} />
            <Route path="/hives/new" element={<AddHivePage />} />
            <Route path="/hives/:hiveId" element={<HiveDetailPage />} />

            {/* The old honey inventory page is gone (V042, #218). Redirect
                rather than 404: it was a real tab for months, so testers
                have it bookmarked and in browser history, and landing on
                "not found" reads as the app losing their honey rather than
                the page having moved. */}
            <Route path="/business/honey" element={<Navigate to="/business/pool" replace />} />
            <Route path="/business/pool" element={<HoneyPoolPage />} />
            <Route path="/business/vendors" element={<VendorsPage />} />
            {/* CLIENTS WAS THE OLD NAME FOR VENDORS (#418 stage 1).
                ClientsPage is deleted: it listed and created clients, and
                VendorsPage does both plus vendor balances, so it was
                strictly superseded. Nothing linked here -- the sidebar has
                pointed at /business/vendors for some time -- but a
                bookmark should not 404, so the path redirects. */}
            <Route path="/business/clients" element={<Navigate to="/business/vendors" replace />} />
            {/* THIS ONE STAYS, AND DELETING IT WOULD BE A BUG (#431).
                It looks as dead as ClientsPage did -- routed, unlinked,
                superseded in appearance by VendorDetail. It is not.

                It is the only caller of POST /clients/{id}/transactions,
                which is the only writer of app.Transactions -- now the
                manual path for income that is not honey (#430: wax,
                propolis, nucs, pollination). Removing it takes away the
                only way to record that income at all.

                PARTLY ANSWERED, AND STILL NOT DELETABLE (3 Oct). #430
                slice 2 gave WAX, PROPOLIS AND POLLEN a real home -- the
                generalised chain, sized and pooled and sold like honey.
                The other two did not come with it: nucs and pollination
                were split to #478 as post-release, so app.Transactions
                is still the ONLY way to record their income, and this
                page is still its only writer.
                
                So the condition on this comment is now #478, not #430.
                Worth being exact about, because "delete it when #430
                lands" reads as satisfied and #430 HAS landed -- and the
                deletion it invites would make the P&L's manual income
                permanently unwritable. That is the trap #431 found the
                first time, from the other direction.
                
                Until then this is reachable only by typing a URL, which
                is its own problem and now #478's to solve. */}
            <Route path="/business/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/business/expenditures" element={<ExpendituresPage />} />
            {/* The other-products door (#430 slice 2). The Sidebar hides
                its link until the account has a non-honey product, but the
                ROUTE is unconditional -- a bookmark or a link somebody
                kept must not 404, and the page says what to do when there
                is nothing to show. */}
            <Route path="/business/products" element={<OtherProductsPage />} />
            <Route path="/business/harvests" element={<HarvestsPage />} />
            <Route path="/business/equipment" element={<EquipmentPage />} />
            <Route path="/business/suppliers" element={<SuppliersPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/business/recurring-orders" element={<RecurringOrdersPage />} />
            <Route path="/business/pnl" element={<PnLPage />} />
            {/* FOLDED INTO P&L (#453), so the answer is on the page a
                bookmark should now land on rather than a 404. Same
                treatment as /business/clients and /business/forecast. */}
            <Route path="/business/break-even" element={<Navigate to="/business/pnl" replace />} />
            {/* HIDDEN, NOT DELETED (#449). The page is unreachable from the
                nav; the path redirects rather than 404s, for the reason
                /business/clients does two dozen lines up -- a bookmark that
                used to work should land somewhere, and Profit & Loss is the
                adjacent money question and the one that now reads REAL
                income since #431.

                ForecastPage.jsx and the endpoint both stay. This is a
                decision about what is offered, not a deletion, and #449 is
                where it comes back. */}
            <Route path="/business/forecast" element={<Navigate to="/business/pnl" replace />} />

            <Route path="/team" element={<TeamDashboardPage />} />
            <Route path="/team/tasks" element={<TeamTasksPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/import" element={<ImportPage />} />
            {/* Invite emails sent before 2026-08-14 point here, and it was
                never a route -- so every one of them dropped the recipient
                on the dashboard with nothing to accept. New emails link to
                /team; this catches the ones already in people's inboxes.
                Kept indefinitely: an invite email can sit unread for weeks,
                and a dead link is how someone concludes the invite was
                fake. */}
            <Route path="/account/shares" element={<Navigate to="/team" replace />} />

            <Route path="/bloom" element={<BloomCalendarPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/checklists" element={<InspectionChecklistsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </AppLayout>
        )}
      </SubscriptionProvider>
    </FeatureThemeProvider>
    </AccountProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthBridge />
      <ColorSchemeProvider>
        <ToastProvider>
          <AuthenticatedTemplate>
            <BrowserRouter>
              <AuthedApp />
            </BrowserRouter>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <LoginScreen />
          </UnauthenticatedTemplate>
        </ToastProvider>
      </ColorSchemeProvider>
    </AuthProvider>
  );
}
